/**
 * @fileoverview Resolves a signed-in user's banking data lock into branch-filter behavior.
 * @description A pure function rather than a hook that reads the session itself: this feature may
 * only import from `shared`/`config` (ADR-0002's `boundaries/dependencies` rule disallows one
 * feature importing another, including via a barrel), so it cannot call `features/auth`'s
 * `useCurrentSession()` directly. Each Banking page instead reads `useCurrentSession().data`'s
 * optional `bankingScope` itself (pages may import any feature's barrel) and passes it in here — the
 * `BankingAccessLock` parameter type below intentionally mirrors `features/auth`'s
 * `CurrentPrincipal['bankingScope']` shape structurally rather than importing it, so this file has
 * zero cross-feature dependency.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import {
  ALL_BRANCHES_ID,
  branchesInRegion,
  BRANCHES,
  regionId,
} from '@/features/banking/constants/banking-options.constants'

import type { Branch } from '@/features/banking/constants/banking-options.constants'

/** Structurally identical to `features/auth`'s `CurrentPrincipal['bankingScope']` — see the file
 * comment for why this isn't imported from there directly. */
export type BankingAccessLock =
  { type: 'branch'; branchId: string } | { type: 'region'; location: Branch['location'] }

export interface BankingAccessScope {
  /** The branch-filter value a page should initialize its state to. */
  defaultBranchId: string
  /** True only for a branch lock — the filter can't be changed at all. */
  locked: boolean
  /** The exact option list the branch filter should offer, or `null` for the unrestricted default
   * (every real branch, plus "All Branches"). */
  options: { value: string; label: string }[] | null
  /** Set only for a region lock — the real branch ids within that region (never includes the
   * region's own pseudo-id), for the handful of pages that rank/aggregate across an explicit branch
   * id list rather than reading through `core()`. */
  regionBranchIds?: string[]
}

const UNRESTRICTED_SCOPE: BankingAccessScope = {
  defaultBranchId: ALL_BRANCHES_ID,
  locked: false,
  options: null,
}

/**
 * @description Turns the signed-in user's `bankingScope` (if any) into concrete branch-filter
 * behavior for a page to apply.
 * @param {BankingAccessLock | undefined} lock - The session's `bankingScope`, or `undefined` for an
 * unrestricted (bank-wide) role.
 * @returns {BankingAccessScope} What the page's branch filter should default to, whether it's
 * locked, and which options it may offer.
 */
export function resolveBankingAccessScope(lock: BankingAccessLock | undefined): BankingAccessScope {
  if (!lock) return UNRESTRICTED_SCOPE

  if (lock.type === 'branch') {
    const branch = BRANCHES.find((candidate) => candidate.id === lock.branchId)
    return {
      defaultBranchId: lock.branchId,
      locked: true,
      options: [{ value: lock.branchId, label: branch?.name ?? lock.branchId }],
    }
  }

  const regionBranches = branchesInRegion(lock.location)
  return {
    defaultBranchId: regionId(lock.location),
    locked: false,
    options: [
      { value: regionId(lock.location), label: `All ${lock.location} Branches` },
      ...regionBranches.map((branch) => ({ value: branch.id, label: branch.name })),
    ],
    regionBranchIds: regionBranches.map((branch) => branch.id),
  }
}
